import { DataHealthBanner } from "@/components/DataHealth";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, Filter, Handshake, ShieldCheck, FileCheck2, Package, ArrowRight, Check, Hourglass, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { PlantSetupBanner } from "@/components/PlantSetupBanner";
import { cn } from "@/lib/utils";
import { SnapshotButton } from "@/components/SnapshotButton";
import { ReimportCard } from "@/components/Reimport";
import { ClearanceCard } from "@/components/Clearance";

const ICON = { data: Database, scope: Filter, suppliers: Handshake, risk: ShieldCheck, dds: FileCheck2, packs: Package };

function Countdown({ days, scope, role }) {
  const pct = Math.max(0, Math.min(100, 100 - (days / 365) * 100));
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#1C1C1E] p-6 text-white" data-testid="readiness-countdown">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#D52B3B]/20 blur-2xl" />
      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">EUDR applies 30 Dec 2026</p>
      <p className="mt-2 font-display text-5xl font-semibold tabular-nums" data-testid="readiness-days-left">{days}<span className="ml-2 text-base font-normal text-slate-400">days left</span></p>
      <div className="mt-4 h-1.5 rounded-full bg-white/10"><div className="h-full rounded-full bg-[#D52B3B] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <p className="mt-3 text-xs text-slate-400" data-testid="readiness-scope-label">{ROLE_LABELS[role]} view · {scope}</p>
    </div>
  );
}

function Stage({ s, i, last }) {
  const Icon = ICON[s.key];
  const tone = { done: "bg-emerald-500 text-white", in_progress: "bg-[#D52B3B] text-white", not_started: "bg-slate-200 text-slate-500", empty: "bg-slate-100 text-slate-400", no_data: "bg-red-100 text-red-700" }[s.state];
  return (
    <div className="rise relative flex-1" style={{ animationDelay: `${i * 70}ms` }} data-testid={`stage-${s.key}`}>
      {!last && <div className="absolute left-[calc(50%+22px)] right-[calc(-50%+22px)] top-5 h-0.5 bg-slate-200"><div className="h-full bg-emerald-400 transition-[width] duration-700" style={{ width: `${s.state === "done" ? 100 : 0}%` }} /></div>}
      <div className="flex flex-col items-center text-center">
        <span className={cn("relative z-10 flex h-10 w-10 items-center justify-center rounded-full ring-4 ring-[#F8FAFC] transition-colors", tone)}>{s.state === "done" ? <Check className="h-5 w-5" /> : <Icon className="h-4 w-4" />}</span>
        <p className="mt-2 text-sm font-semibold text-slate-800">{s.label}</p>
        <p className="font-display text-xl font-semibold tabular-nums text-slate-900" data-testid={`stage-${s.key}-pct`}>{s.state === "no_data" ? "No data" : s.state === "empty" ? "—" : `${s.pct}%`}</p>
        <div className="mt-1 space-y-0.5">{s.numbers.map((n) => <p key={n} className="text-[11px] leading-tight text-slate-500">{n}</p>)}</div>
      </div>
    </div>
  );
}

function Action({ a, i }) {
  return (
    <Link to={a.to} data-testid={`next-action-${i}`} className="rise group flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-[#D52B3B]/40 hover:shadow-md" style={{ animationDelay: `${150 + i * 60}ms` }}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#D52B3B]/10 font-display text-lg font-semibold text-[#D52B3B] tabular-nums">{a.count > 999 ? `${Math.round(a.count / 100) / 10}k` : a.count}</span>
      <p className="flex-1 text-sm font-medium text-slate-800">{a.text}</p>
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#D52B3B]">{a.button}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
    </Link>
  );
}

export default function Readiness() {
  const { user } = useAuth();
  const { plants } = usePlant();
  const [plant, setPlant] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/readiness", { params: plant ? { plant } : {} }).then((r) => setD(r.data)); }, [plant]);
  const mine = user?.role === "purchasing" ? plants.filter((p) => !user.plants?.length || user.plants.includes(p.code)) : plants;
  const scope = plant ? `plant ${plant}` : user?.role === "purchasing" ? "all my plants" : "all plants";
  return (
    <div data-testid="readiness-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D52B3B]">Readiness tracker</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-900">Where are we?</h1>
          <p className="mt-1 text-sm text-slate-500">Live from SAP data, supplier portal, risk and DDS – each card opens the exact list behind its number.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SnapshotButton testid="readiness-download-snapshot" />
          <select data-testid="readiness-plant-select" value={plant} onChange={(e) => setPlant(e.target.value)} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm">
            <option value="">{user?.role === "purchasing" ? "All my plants" : "All plants"}</option>
            {mine.map((p) => <option key={p.code} value={p.code}>Plant {p.code}{p.name && p.name !== `Plant ${p.code}` ? ` · ${p.name}` : ""}</option>)}
          </select>
        </div>
      </div>
      <PlantSetupBanner />
      {!d ? <div className="h-72 animate-pulse rounded-2xl bg-slate-100" data-testid="readiness-loading" /> : (
        <>
          <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
            <Countdown days={d.days_left} scope={scope} role={user?.role} />
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="readiness-stepper">
              <DataHealthBanner health={d.health} />
              <div className="flex gap-2">{d.stages.map((s, i) => <Stage key={s.key} s={s} i={i} last={i === d.stages.length - 1} />)}</div>
            </div>
          </div>
          <div>
            <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-800 md:text-lg"><Hourglass className="h-4 w-4 text-[#D52B3B]" />Next best actions</h2>
            {d.actions.length ? <div className="grid gap-3 xl:grid-cols-2" data-testid="next-actions">{d.actions.map((a, i) => <Action key={a.text} a={a} i={i} />)}</div> : (
              <div className="flex items-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8" data-testid="next-actions-empty">
                <PartyPopper className="h-8 w-8 text-[#D52B3B]" /><div><p className="font-semibold text-slate-800">Nothing for you to do right now</p><p className="text-sm text-slate-500">Every stage you can act on in this scope is up to date.</p></div>
              </div>)}
          </div>
          <ReimportCard params={plant ? { plant } : {}} testid="readiness-reimport" />
          <ClearanceCard params={plant ? { plant } : {}} testid="readiness-clearance" />
          <div className="flex flex-wrap gap-2" data-testid="readiness-extracts">
            {d.extracts.map((x) => <span key={x.type} className={cn("rounded-full border px-3 py-1 text-xs", x.fresh ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500")}>{x.label}: {x.fresh ? "fresh" : x.at ? "older than 26h" : "never uploaded"}</span>)}
          </div>
        </>)}
    </div>
  );
}
